import { Fragment, type ReactNode } from "react";

/** Splits a template on **bold** spans and {name} placeholders, keeping both. */
const COPY_TOKEN = /(\*\*[^*]+\*\*|\{[a-zA-Z]+\})/g;

/**
 * Splits a multi-paragraph block from copy.ts on its blank lines, so a block of
 * prose can be written and edited as one string rather than an array of them.
 */
export function copyParagraphs(body: string): string[] {
  return body.trim().split(/\n\s*\n/);
}

/**
 * Substitutes `{name}` placeholders and returns a plain string, for the callers
 * that need one: the metric footers are typed as `string[]` and are read back by
 * a `key` prop, so they can't be markup. Emphasis isn't handled here, because a
 * string that needs it has to come through `renderCopy` instead.
 */
export function fillCopy(
  template: string,
  values: Record<string, string | number>
): string {
  return template.replace(/\{([a-zA-Z]+)\}/g, (match, name: string) =>
    name in values ? String(values[name]) : match
  );
}

/**
 * Renders a string from copy.ts: substitutes `{name}` placeholders with the
 * values the caller supplies, and turns `**...**` into an emphasised span.
 *
 * This is what lets every string stay plain text in one editable file while a
 * few of them still carry emphasis and interpolated figures. Strings with
 * neither — most of them — are rendered directly and never come through here.
 *
 * Recurses so a placeholder can sit inside an emphasised span, which the lift
 * tags rely on: "a **{pct}% lift** from ...".
 */
export function renderCopy(
  template: string,
  values: Record<string, ReactNode> = {},
  emphasisClassName = "font-medium"
): ReactNode {
  return template.split(COPY_TOKEN).map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <span key={index} className={emphasisClassName}>
          {renderCopy(part.slice(2, -2), values, emphasisClassName)}
        </span>
      );
    }
    if (part.startsWith("{") && part.endsWith("}")) {
      return <Fragment key={index}>{values[part.slice(1, -1)]}</Fragment>;
    }
    return part;
  });
}
